import { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch, batch, useSelector } from 'react-redux';
import { isBrowser } from 'ws-scripts/modules/environment';
import { usePrefs } from 'wsm-common-data';
import { wiapiMediaUpdated } from '../actions/media';

/**
 * Hook to handle WIAPI vehicle media updates
 *
 * @param {Object} options Configuration object
 * @param {Object} options.setCarouselIndex Function to set the current slider active index
 * @param {Object} options.carouselIndex Current slider active index
 * @param {Object} options.openModal Current modal status
 * @returns {Object} Hook state and handlers
 */
export default function useWiapiVehicleMedia({
	setCarouselIndex,
	carouselIndex,
	openModal
}) {
	const dispatch = useDispatch();
	const media = useSelector((state) => state.media);
	const widgetInfo = useSelector((state) => state.widgetInfo);
	const prefs = usePrefs();

	const [disableAnimation, setDisableAnimation] = useState(false);
	const widgetElementRef = useRef(
		isBrowser
			? document.querySelector('[data-widget-name="ws-vehicle-gallery"]')
			: null
	);

	/**
	 * Update WIAPI media using Redux action
	 */
	const updateWiapiMedia = useCallback(
		(
			galleryContent,
			offsetSharedVehicleImageByOne,
			useStockPhotosForOffsiteInventory
		) => {
			dispatch(
				wiapiMediaUpdated(
					galleryContent,
					offsetSharedVehicleImageByOne,
					useStockPhotosForOffsiteInventory
				)
			);
		},
		[dispatch]
	);

	/**
	 * Handler function for WIAPI vehicle media updates
	 */
	const handleWiapiVehicleMediaUpdated = useCallback(() => {
		const { vin } = widgetInfo.vehicle;
		const {
			offsetSharedVehicleImageByOne,
			useStockPhotosForOffsiteInventory
		} = prefs;
		const oldImagesToDisplay = media.imagesToDisplay;

		// Return early if the WIAPI is not available
		if (
			!(
				window.DDC &&
				window.DDC.PrivateAPI &&
				typeof window.DDC.PrivateAPI.getGalleryContent === 'function'
			)
		) {
			return;
		}

		// Get the gallery content for the current vehicle
		const galleryContent = window.DDC.PrivateAPI.getGalleryContent(
			'vehicle-media',
			vin
		);

		// Return early if no content was found
		if (!galleryContent) {
			return;
		}

		// Return early if no media existed in the content
		if (
			!(
				galleryContent.primary.length > 0 ||
				galleryContent.secondary.length > 0 ||
				galleryContent.last.length > 0
			)
		) {
			return;
		}

		const oldIndex = carouselIndex || 0;

		updateWiapiMedia(
			galleryContent,
			offsetSharedVehicleImageByOne,
			useStockPhotosForOffsiteInventory
		);

		// If the user hasn't interacted with the widget, go to the first primary image inserted (index = 0).
		// If they have interacted with the widget, update the index to show the same image they were seeing before.
		let newIndex = 0;
		if (openModal || oldIndex !== 0) {
			const newImagesToDisplay = media.imagesToDisplay;
			const foundIndex = newImagesToDisplay.findIndex(
				(elem) => elem.src === oldImagesToDisplay[oldIndex]?.src
			);
			// If we can't find the image, just go to the first image
			newIndex = foundIndex !== -1 ? foundIndex : 0;
		}

		// Update render and modal settings
		batch(() => {
			setCarouselIndex(newIndex);
			setDisableAnimation(false);
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only effect for one-time carousel index initialization
	}, []);

	// Set up event listener for WIAPI updates
	useEffect(() => {
		window.addEventListener(
			'wiapiVehicleMediaUpdated',
			handleWiapiVehicleMediaUpdated
		);

		// Check if WIAPI media is available on mount
		handleWiapiVehicleMediaUpdated();

		return () => {
			window.removeEventListener(
				'wiapiVehicleMediaUpdated',
				handleWiapiVehicleMediaUpdated
			);
		};
	}, [handleWiapiVehicleMediaUpdated]);

	return {
		disableAnimation,
		widgetElement: widgetElementRef.current,
		handleWiapiVehicleMediaUpdated
	};
}
